import defaultAvatar from "@/assets/default_avatar.svg";
import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { useRef, useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Modal from "@/common/elements/Modal";
import axios from "axios";
import API_ENDPOINTS from "@/util/api";
import { Form } from "antd";
import api from "@/services/axios_custom";
import Table from "@/common/elements/Table";
import { setBookings, setBookingsFieldsEnabled } from "@/slices/bookingsSlice.js";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
} from "@/util/common_function";
import {
  BookingsSectionTitle,
  InmateDetails,
  InmateInformation,
} from "@/components/InmateHeader/styles.js";
import { Image, Popconfirm } from "antd";
import useDialog from "@/hooks/useDialog.jsx";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

// Lazy-loaded: InmateToVisit -> Bookings -> BookingsDetails -> VisitorLogDetails
// -> AddVisitor -> InmateToVisit is a real circular import chain (Bookings
// embeds a Visitor Log tab that renders this same component). A static
// top-level import would resolve unreliably; lazy() defers it until this
// popup actually opens, same as routes.jsx does for other pages.
const BookingsPage = lazy(() => import("@/pages/Bookings"));

const FILTER_KEY_MAP = {
  lastName:   "lName",
  firstName:  "fName",
  middleName: "mName",
  suffix:     "suffix",
};

const TABLE_HEADERS = [
  { key: "bookingNumber",   text: "Booking No"       },
  { key: "lastName",        text: "Last Name"        },
  { key: "firstName",       text: "First Name"       },
  { key: "middleName",      text: "Middle Name"      },
  { key: "suffix",          text: "Suffix"           },
  { key: "dob",             text: "DOB"              },
  { key: "housingAgency",   text: "Housing Agency"   },
  { key: "currentLocation", text: "Current Location" },
  { key: "housingLocation", text: "Housing Location" },
];

const SELECTED_INMATE_HEADERS = [
  { key: "bookingNumber",    text: "Booking No"  },
  { key: "inmateLastName",   text: "Last Name"   },
  { key: "inmateFirstName",  text: "First Name"  },
  { key: "inmateMiddleName", text: "Middle Name" },
  { key: "inmateSuffix",     text: "Suffix"      },
  { key: "inmatePin",        text: "Pin"         },
];

const InmateToVisit = ({ startingPoint = "" }) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s.bookings);

  const [detailsLoaded, setDetailsLoaded]       = useState(false);
  const [selectedInmate, setSelectedInmate]     = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRow, setDetailsRow]             = useState(null);
  const [showDifferentBooking, setShowDifferentBooking] = useState(false);
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const [searchByBookingNo, setSearchByBookingNo] = useState(false);
  const [bookingNumberSearch, setBookingNumberSearch] = useState("");
  const [tableRows, setTableRows]               = useState([]);
  const [selectedRow, setSelectedRow]           = useState(null);
  const [showModal, setShowModal]               = useState(false);
  const [loading, setLoading]                   = useState(false);
  const [tableKey, setTableKey]                 = useState(0);
  const [searchParams, setSearchParams]         = useState({
    lastName: "", middleName: "", firstName: "", suffix: "",
  });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const detailsCache            = useRef({});
  const formRef                 = useRef(null);
  const bookingDetailsCache     = useRef({});
  const bookingDetailsInFlight  = useRef({});
  const cancelSourceRef         = useRef(null);
  // Snapshot of this Visitor Log's own `bookings` redux state, taken right
  // before the "View Booking Details" popup mounts a second <BookingsPage>
  // that shares the same `state.bookings` slice - restored on close so the
  // read-only popup can't clobber the Visitor Log record being worked on.
  const bookingsSnapshotRef     = useRef(null);

  const [form] = Form.useForm();

  const relationshipOptions = useMemo(() => getOptionsFromRefList("Relationship"), []);
  const suffixOptions       = useMemo(() => getOptionsFromRefList("suffix"), []);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: selectedInmateHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Visitor Log:VisitorLogBooking", SELECTED_INMATE_HEADERS);

  // -----------------------------------------------------------------------
  // Derived preview values from selected row
  // -----------------------------------------------------------------------
  const previewBio   = selectedRow?.rawData?.jackets?.bio?.[0] || null;
  const bioPhotoUrl  = previewBio?.photoUrl;

  // -----------------------------------------------------------------------
  // Selected inmate table rows
  // -----------------------------------------------------------------------
  const selectedInmateRows = (bookings?.visitorLogInmate || []).map((i) => ({
    bookingNumber:      i.bookingNumber,
    jacketId:           i.jacketId,
    inmateLastName:     i.inmateLastName,
    inmateFirstName:    i.inmateFirstName,
    inmateMiddleName:   i.inmateMiddleName,
    inmateSuffix:       i.inmateSuffix,
    inmatePin:          i.inmatePin,
    visitorLogInmateId: i.visitorLogInmateId,
  }));

  // -----------------------------------------------------------------------
  // Sync relationship from Redux → form
  // -----------------------------------------------------------------------
  useEffect(() => {
    // "visitorRelationship" (no trailing "U") — matches convertColumnName's
    // conversion of the raw "Visitor_Relationship_U" column, the same
    // convention every other extra-table field in this app uses (see
    // util/extraColumnActions.js). Was "visitorRelationshipU" here, which
    // never matched what the generic /api/extra/save endpoint expects.
    const relationship = bookings?.visitorLogExtra?.[0]?.visitorRelationship;
    if (!relationship) return;
    form.setFieldsValue({ relationship });
  }, [bookings]);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  const getRelationshipIdFromLabel = (label) =>
    relationshipOptions.find((o) => o.label === label)?.value ?? null;

  const onChangeSearchParams = (eOrValue, explicitName) => {
    if (eOrValue?.target) {
      const { name, value } = eOrValue.target;
      setSearchParams((prev) => ({ ...prev, [name]: value }));
      return;
    }
    if (!explicitName) return;
    setSearchParams((prev) => ({ ...prev, [explicitName]: eOrValue }));
  };

  // Matches the ASP "Inmate Information" dialog header, e.g. "MEN, SLACK SM, II (#2600078)"
  const formatInmateName = (row) => {
    if (!row) return "-";
    const last   = row.inmateLastName   || "";
    const first  = row.inmateFirstName  || "";
    const middle = row.inmateMiddleName || "";
    const suffix = row.inmateSuffix     || "";
    const namePart = [`${last}, ${first} ${middle}`.trim(), suffix]
      .filter(Boolean)
      .join(", ");
    return isNonEmptyVariable(row.bookingNumber)
      ? `${namePart} (#${row.bookingNumber})`
      : namePart || "-";
  };

  const formatRowForTable = (row) => {
    const bio    = Array.isArray(row.bio)           ? row.bio[0]           : row.bio           || {};
    const arrest = Array.isArray(row.arrestReports) ? row.arrestReports[0] : row.arrestReports || {};
    return {
      bookingNumber:   row.bookingNumber  || "-",
      lastName:        row.lastname       || row.lastName || bio.lName || "-",
      firstName:       row.firstName      || bio.fName   || "-",
      middleName:      row.middleName     || bio.mName   || "-",
      suffix:          row.suffix         || bio.suffix  || "-",
      dob:             row.dob            || bio.dob     || "-",
      housingAgency:   row.housingAgency  || arrest.housingAgency  || "-",
      currentLocation: row.currentLocation || arrest.currentLocation || "-",
      housingLocation: row.housingLocation ||
        (arrest.housingAssignmentBlock && arrest.housingAssignmentCell && arrest.housingAssignmentBed
          ? `${arrest.housingAssignmentBlock} ${arrest.housingAssignmentCell} ${arrest.housingAssignmentBed} {${arrest.housingAgency}}`
          : "-"),
      rawData: row,
    };
  };

  const normalizeBookingDetails = (data) => {
    const arrest = data?.arrestReports?.[0] || {};
    const jacket = data?.jackets?.[0]       || {};
    const bio    = data?.bio?.[0]           || {};
    const charge = data?.charges?.[0]       || {};
    const inmate = data?.inmateTracking?.[0] || {};

    // Same source/logic as the Bookings header's "CLASS" badge (InmateHeader/index.jsx
    // findClassLevel()): the most recent InmateClassificationQuestionnaire's
    // classificationID, resolved against the "Classifications" ref list.
    // ArrestReports.Classification_ID is a separate, unrelated field - not this.
    const classificationQuestionnaires = data?.inmateClassificationQuestionnaires || [];
    const latestClassificationId =
      classificationQuestionnaires[classificationQuestionnaires.length - 1]?.classificationID ?? null;

    return {
      bookingId:             data.bookingId     || null,
      bookingNumber:         data.bookingNumber || null,
      jacketId:              jacket.jacketId    || null,
      bioId:                 bio.bioId          || null,
      inmateFirstName:       bio.fName          || null,
      inmateLastName:        bio.lName          || null,
      inmateMiddleName:      bio.mName          || null,
      inmateSuffix:          bio.suffix         || null,
      inmatePin:             bio.pinNo          || null,
      dateAdded:             data.dateAdded     || null,
      timeAdded:             data.timeAdded     || null,
      arrestDate:            arrest.arrestDate  || null,
      arrestTime:            arrest.arrestTime  || null,
      scheduledReleaseDate:  arrest.scheduledReleaseDate || charge.scheduledReleaseDate || null,
      scheduledReleaseTime:  arrest.scheduledReleaseTime || null,
      classificationId:      latestClassificationId,
      classificationName:    getRefListNameByID("Classifications", latestClassificationId, false) || "-",
      assignedLocation:      (inmate.inJailType + "" + (inmate.block || "")) || null,
      currentLocation:       arrest.currentLocation     || null,
      dob:                   bio.dob || null,
      bio,
    };
  };

  // -----------------------------------------------------------------------
  // Search
  // -----------------------------------------------------------------------
  const onSearchClick = async (e) => {
    e.preventDefault();
    if (cancelSourceRef.current) cancelSourceRef.current.cancel();
    const source = axios.CancelToken.source();
    cancelSourceRef.current = source;

    try {
      setLoading(true);
      let rows = [];

      if (searchByBookingNo) {
        if (!bookingNumberSearch) return;
        const res = await api.get(
          `${API_ENDPOINTS.getBookingByID}${bookingNumberSearch}`,
          { cancelToken: source.token }
        );
        if (res.data?.bookingNumber) rows = [formatRowForTable(res.data)];
      } else {
        const basePayload = Object.entries(searchParams)
          .filter(([, v]) => v)
          .map(([k, v]) => ({ filterKey: FILTER_KEY_MAP[k], value: v }));

        const payload = basePayload.some((p) => p.filterKey === "searchType")
          ? basePayload
          : [...basePayload, { filterKey: "searchType", value: "Starts With" }];

        if (!payload.length) return;

        const res = await api.post(
          `${API_ENDPOINTS.bookingsSearch}?size=500&direction=desc`,
          payload,
          { cancelToken: source.token }
        );
        rows = res?.data?.rows || [];
      }

      setTableRows(rows.map(formatRowForTable));
      setSelectedRow(null);
      setTableKey((k) => k + 1);
    } catch (err) {
      if (!axios.isCancel(err)) console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Row selection with caching
  // -----------------------------------------------------------------------
  const onRowClick = async (row) => {
    const { bookingNumber } = row;
    if (selectedRow?.bookingNumber === bookingNumber) {
      setSelectedRow(null);
      setDetailsLoaded(false);
      return;
    }
    if (bookingDetailsCache.current[bookingNumber]) {
      setSelectedRow({ ...row, ...bookingDetailsCache.current[bookingNumber] });
      setDetailsLoaded(true);
      return;
    }
    if (bookingDetailsInFlight.current[bookingNumber]) return;
    bookingDetailsInFlight.current[bookingNumber] = true;

    try {
      setDetailsLoaded(false);
      const res = await api.get(`${API_ENDPOINTS.getBookingByID}${bookingNumber}`);
      const normalized = normalizeBookingDetails(res.data);
      bookingDetailsCache.current[bookingNumber] = normalized;
      setSelectedRow({ ...row, ...normalized });
      setDetailsLoaded(true);
    } catch (err) {
      console.error("Booking details fetch failed", err);
    } finally {
      delete bookingDetailsInFlight.current[bookingNumber];
    }
  };

  // -----------------------------------------------------------------------
  // Select inmate
  // -----------------------------------------------------------------------
  const onSelectCurrentFolder = () => {
    if (!selectedRow) return;

    const newInmate = {
      visitorLogInmateId: null,
      bookingNumber:      selectedRow.bookingNumber,
      bioId:              selectedRow.bioId,
      inmateFirstName:    selectedRow.inmateFirstName,
      inmateLastName:     selectedRow.inmateLastName,
      inmateMiddleName:   selectedRow.inmateMiddleName,
      inmatePin:          selectedRow.inmatePin,
      inmateSuffix:       selectedRow.inmateSuffix,
      jacketId:           selectedRow.jacketId,
      visitorLogId:       bookings.visitorLogId,
      visitorLogInmateExtra: [],
    };

    const inmateExists = bookings.visitorLogInmate?.some(
      (i) => i.bookingNumber === newInmate.bookingNumber
    );
    if (inmateExists) { handleClose(); return; }

    const newVisitorLogBooking = {
      visitorLogBookingId: null,
      visitorLogId:        bookings.visitorLogId ?? null,
      bookingId:           selectedRow.bookingId ?? null,
      visitorLog:          [],
    };

    const bookingExists = bookings.visitorLogBooking?.some(
      (b) => b.bookingId === selectedRow.bookingId
    );

    dispatch(setBookings({
      ...bookings,
      bookingId: selectedRow.bookingId || null,
      visitorLogInmate: [...(bookings.visitorLogInmate || []), newInmate],
      visitorLogBooking: bookingExists
        ? bookings.visitorLogBooking
        : [...(bookings.visitorLogBooking || []), newVisitorLogBooking],
    }));

    handleClose();
  };

  // -----------------------------------------------------------------------
  // Delete inmate
  // -----------------------------------------------------------------------
  const onDeleteInmate = async () => {
    if (!selectedInmate) return;

    const content = (
      <>
        <p>Are you sure you want to delete this inmate?</p>
        <p>Visitor Log Booking No: <strong>{selectedInmate?.bookingNumber ?? "-"}</strong></p>
        <p>Inmate Name: <strong>{`${selectedInmate?.inmateLastName ?? ""} ${selectedInmate?.inmateFirstName ?? ""}`.trim() || "-"}</strong></p>
      </>
    );

    const canDelete = await new Promise((resolve) => {
      showDialog("confirm", {
        title: "Warning", content,
        onConfirm: () => resolve(true),
        onCancel:  () => resolve(false),
      });
    });
    if (!canDelete) return;

    try {
      setLoading(true);
      const { visitorLogInmateId } = selectedInmate;
      if (visitorLogInmateId) {
        await api.delete(`${API_ENDPOINTS.visitorLog.deleteInmateByID}/${visitorLogInmateId}`);
      }
      const updatedInmates = (bookings.visitorLogInmate || []).filter(
        (i) => i.bookingNumber !== selectedInmate.bookingNumber
      );
      dispatch(setBookings({ ...bookings, visitorLogInmate: updatedInmates }));
      setSelectedInmate(null);
    } catch (err) {
      console.error("Failed to delete inmate", err);
      showDialog("alert", { title: "Error", content: "Failed to delete inmate. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // Details modal
  // -----------------------------------------------------------------------
  const onDetailsClick = async (value) => {
    const data = value.bookingNumber ? value : selectedInmate;
    if (!data?.bookingNumber) return;
    if (value.bookingNumber) setSelectedInmate(value);

    setShowDifferentBooking(false);
    setTableRows([]);
    setSelectedRow(null);

    const { bookingNumber } = data;
    if (detailsCache.current[bookingNumber]) {
      setDetailsRow(detailsCache.current[bookingNumber]);
      setShowDetailsModal(true);
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(`${API_ENDPOINTS.getBookingByID}${bookingNumber}`);
      const normalized = normalizeBookingDetails(res.data);
      detailsCache.current[bookingNumber] = normalized;
      setDetailsRow(normalized);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Failed to load inmate details", err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------------
  // "Different Booking" — swap which booking/inmate this list entry points to
  // (mirrors ASP's SFLOSupport_Bookings "Different Booking" button, used by
  // VisitorLog_Inmate.asp's Inmate Information dialog)
  // -----------------------------------------------------------------------
  const onDifferentBookingClick = () => {
    setSearchByBookingNo(false);
    setBookingNumberSearch("");
    setSearchParams({ lastName: "", middleName: "", firstName: "", suffix: "" });
    setTableRows([]);
    setSelectedRow(null);
    setShowDifferentBooking(true);
  };

  const onCancelDifferentBooking = () => {
    setShowDifferentBooking(false);
    setTableRows([]);
    setSelectedRow(null);
  };

  const onConfirmDifferentBooking = () => {
    if (!selectedRow || !selectedInmate) return;

    const updatedInmate = {
      ...selectedInmate,
      bookingNumber:    selectedRow.bookingNumber,
      bioId:            selectedRow.bioId,
      inmateFirstName:  selectedRow.inmateFirstName,
      inmateLastName:   selectedRow.inmateLastName,
      inmateMiddleName: selectedRow.inmateMiddleName,
      inmatePin:        selectedRow.inmatePin,
      inmateSuffix:      selectedRow.inmateSuffix,
      jacketId:         selectedRow.jacketId,
    };

    const updatedInmates = (bookings.visitorLogInmate || []).map((i) =>
      i.bookingNumber === selectedInmate.bookingNumber ? updatedInmate : i
    );
    dispatch(setBookings({ ...bookings, visitorLogInmate: updatedInmates }));

    detailsCache.current[selectedRow.bookingNumber] = selectedRow;
    setDetailsRow(selectedRow);
    setSelectedInmate(updatedInmate);
    setShowDifferentBooking(false);
    setTableRows([]);
    setSelectedRow(null);
  };

  // "..." button next to the name bar — mirrors ASP's SFLOSupport.vbs
  // prvSFLO_SubObjectDetails, which opens the linked Booking read-only
  // (Browse mode), not editable. Shown as a popup, same pattern Booking
  // Queue already uses to embed <BookingsPage> inside a Modal instead of
  // navigating away (see pages/BookingQueue/index.jsx).
  const onViewBookingDetails = () => {
    if (!detailsRow?.bookingNumber) return;
    bookingsSnapshotRef.current = { bookings, bookingsFieldsEnabled };
    // showBookings/bookings are the SAME shared redux flags this Visitor
    // Log page itself uses to decide list-vs-detail - resetting them from
    // here (tried once) also flips this page back to its own list view,
    // since it reads the exact same booleans. The loading-state fix for the
    // popup below belongs entirely inside <BookingsPage>'s own mount logic
    // instead, so it doesn't reach back out and disturb this page's state.
    sessionStorage.setItem("open-booking-number", detailsRow.bookingNumber);
    setShowBookingDetailsModal(true);
  };

  const onCloseBookingDetailsModal = () => {
    setShowBookingDetailsModal(false);
    // Restore this Visitor Log's own booking - <BookingsPage> just loaded a
    // different one into the same shared `state.bookings` slice.
    if (bookingsSnapshotRef.current) {
      dispatch(setBookings(bookingsSnapshotRef.current.bookings));
      dispatch(setBookingsFieldsEnabled(bookingsSnapshotRef.current.bookingsFieldsEnabled));
      bookingsSnapshotRef.current = null;
    }
  };

  const handleDetailsClose = () => {
    setShowDetailsModal(false);
    setShowDifferentBooking(false);
    setTableRows([]);
    setSelectedRow(null);
  };

  // -----------------------------------------------------------------------
  // Relationship change → Redux
  // -----------------------------------------------------------------------
  const handleRelationshipChange = (label) => {
    // Merges onto whatever's already in bookings.visitorLogExtra[0] (other
    // "_U" fields VisitorInformation.jsx's generic section may have already
    // set/fetched) rather than replacing the object outright.
    const existingExtra = bookings?.visitorLogExtra?.[0] || {};
    dispatch(setBookings({
      ...bookings,
      visitorLogExtra: [{
        ...existingExtra,
        visitorLogExtraId: existingExtra?.visitorLogExtraId ?? 0,
        visitorLogId:      bookings?.visitorLogId ?? 0,
        // "visitorRelationship" (no trailing "U") — see the matching note
        // on the read side above.
        visitorRelationship: getRelationshipIdFromLabel(label),
      }],
    }));
  };

  // -----------------------------------------------------------------------
  // Modal / state reset
  // -----------------------------------------------------------------------
  const resetState = () => {
    setSearchByBookingNo(false);
    setBookingNumberSearch("");
    setSearchParams({ lastName: "", middleName: "", firstName: "", suffix: "" });
    formRef.current?.reset();
    setTableRows([]);
    setSelectedRow(null);
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleClose = () => { resetState(); setShowModal(false); };

  const onSelectedInmateRowClick = (row) => {
    setSelectedInmate((prev) =>
      prev?.bookingNumber === row.bookingNumber ? null : row
    );
  };

  // -----------------------------------------------------------------------
  // Inmate detail row renderer
  // -----------------------------------------------------------------------
  const InmateDetailRow = ({ label, value }) => (
    <InmateInformation>
      <div className="key">{label}</div>
      <div className="value justify-content-between">
        {isNonEmptyVariable(value) ? <span>{value}</span> : <> - </>}
      </div>
    </InmateInformation>
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      {/* ── Selected inmates list ───────────────────────────────────── */}
      <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "15px" }}>
        <span>List of Inmates associated with this visit</span>
      </BookingsSectionTitle>

      <StyledGridRow justify="end">
        <StyledGridCol>
          <Button style={{ marginRight: 8 }} onClick={() => setShowModal(true)} disabled={!bookingsFieldsEnabled || isConfiguring}>
            ADD
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button style={{ marginRight: 8 }} disabled={!selectedInmate || isConfiguring} onClick={onDetailsClick}>
            Details
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button disabled={!bookingsFieldsEnabled || !selectedInmate || isConfiguring} onClick={onDeleteInmate}>
            Delete
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button htmlType="button" onClick={startConfiguring}>
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Save the current column layout?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button htmlType="button">{configButtonLabel}</Button>
            </Popconfirm>
          )}
        </StyledGridCol>
      </StyledGridRow>

      <Table
        ref={dataTableRef}
        headers={selectedInmateHeaders}
        draggable={isConfiguring}
        data={selectedInmateRows}
        noDataMessage="No inmates added yet"
        style={{ marginTop: "15px" }}
        onRowClick={onSelectedInmateRowClick}
        onRowDoubleClick={onDetailsClick}
      />

      {/* ── Relationship field — the only secured field in this component */}
      <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "15px" }}>
        <span>Additional Visitor Log information</span>
      </BookingsSectionTitle>

      <FieldSecurityProvider
        entity="VisitorLog"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form form={form}>
          <StyledGridRow justify="space-between">
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName="relationship"
                formItemProps={{ name: "relationship", label: "Relationship" }}
              >
                <Select
                  options={relationshipOptions}
                  placeholder="Relationship"
                  showSearch={false}
                  variant="outlined"
                  onChange={handleRelationshipChange}
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>

      {/* ── Add Inmate modal — search UI only, no field security needed ── */}
      <Modal
        header="Add Inmate"
        show={showModal}
        size="xl"
        onHide={handleClose}
        footerActions={[{
          key: "select", text: "Select Current Folder",
          onClick: onSelectCurrentFolder, disabled: !selectedRow,
        }]}
        footerCloseActionText="Close"
      >
        <form ref={formRef}>
          <Row>
            <Col lg={12}>
              <FormItem>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={searchByBookingNo}
                    onChange={(e) => {
                      setSearchByBookingNo(e.target.checked);
                      setBookingNumberSearch("");
                      setSearchParams({ lastName: "", middleName: "", firstName: "", suffix: "" });
                    }}
                  />
                  Search by Booking Number
                </label>
              </FormItem>
            </Col>
          </Row>

          {searchByBookingNo ? (
            <Row>
              <Col lg={6}>
                <FormItem label="Booking Number">
                  <TextField
                    value={bookingNumberSearch}
                    onChange={(e) => setBookingNumberSearch(e.target.value)}
                    placeholder="Enter booking number"
                  />
                </FormItem>
              </Col>
              <Col lg={2}>
                <Button style={{ marginTop: 28 }} onClick={onSearchClick}>Search</Button>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col lg={3}>
                <FormItem label="Last Name">
                  <TextField name="lastName" value={searchParams.lastName} onChange={onChangeSearchParams} />
                </FormItem>
              </Col>
              <Col lg={3}>
                <FormItem label="Middle Name">
                  <TextField name="middleName" value={searchParams.middleName} onChange={onChangeSearchParams} />
                </FormItem>
              </Col>
              <Col lg={3}>
                <FormItem label="First Name">
                  <TextField name="firstName" value={searchParams.firstName} onChange={onChangeSearchParams} />
                </FormItem>
              </Col>
              <Col lg={2}>
                <FormItem label="Suffix">
                  <Select options={suffixOptions} value={searchParams.suffix} onChange={(opt) => onChangeSearchParams(opt, "suffix")} />
                </FormItem>
              </Col>
              <Col lg={1}>
                <Button style={{ marginTop: 28 }} onClick={onSearchClick}>Search</Button>
              </Col>
            </Row>
          )}
        </form>

        {/* Selected inmate preview */}
        {selectedRow && (
          <div style={{ marginTop: 20, marginBottom: 20, background: "var(--background-color)" }}>
            <Row>
              <Col lg={3}>
                <Image
                  alt="Default Inmate Avatar" className="avatar"
                  src={bioPhotoUrl} width={200} height={200} fallback={defaultAvatar}
                />
              </Col>
              <Col lg={9}>
                <InmateDetails fluid>
                  <Row style={{ marginTop: "10px" }}>
                    <InmateDetailRow label="Arrest Date"            value={selectedRow?.arrestDate}            />
                    <InmateDetailRow label="Scheduled Release Date" value={selectedRow?.scheduledReleaseDate}  />
                    <InmateDetailRow label="Classification"         value={selectedRow?.classificationName}    />
                  </Row>
                  <Row style={{ marginTop: "10px" }}>
                    <InmateDetailRow label="Assigned Location" value={selectedRow?.assignedLocation} />
                    <InmateDetailRow label="Current Location"  value={selectedRow?.currentLocation}  />
                  </Row>
                  <Row style={{ marginTop: "10px" }}>
                    <InmateDetailRow label="Pin No"         value={selectedRow?.inmatePin}     />
                    <InmateDetailRow label="Booking Number" value={selectedRow?.bookingNumber}  />
                    <InmateDetailRow label="DOB"            value={selectedRow?.dob}           />
                  </Row>
                </InmateDetails>
              </Col>
            </Row>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Table
            key={tableKey}
            headers={TABLE_HEADERS}
            data={tableRows}
            totalRecords={tableRows.length}
            isAPiCallLoading={loading}
            pagination={pagination}
            setPagination={setPagination}
            onRowClick={onRowClick}
            noDataMessage="No inmates found"
          />
        </div>
      </Modal>

      {/* ── Inmate Details modal — display only, no field security ──── */}
      <Modal
        header="Inmate Details"
        show={showDetailsModal}
        size="xl"
        onHide={handleDetailsClose}
        footerActions={
          showDifferentBooking
            ? [
                { key: "cancel", text: "Cancel", onClick: onCancelDifferentBooking },
                { key: "select", text: "Select", onClick: onConfirmDifferentBooking, disabled: !selectedRow },
              ]
            : [{ key: "close", text: "Close", onClick: handleDetailsClose }]
        }
      >
        {/* Name / booking-number bar + Different Booking button — matches the
            ASP "Inmate Information" dialog header (VisitorLog_Inmate.asp /
            SFLOSupport_Bookings.asp) */}
        {detailsRow && (
          <StyledGridRow style={{ marginBottom: 10 }}>
            <StyledGridCol flex="auto">
              {showDifferentBooking ? (
                <BookingsSectionTitle style={{ margin: 0 }}>
                  <span>Enter Inmate Information</span>
                </BookingsSectionTitle>
              ) : (
                <InmateInformation style={{ gap: 0 }}>
                  <div className="value red" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <h5 style={{ margin: 0 }}>{formatInmateName(detailsRow)}</h5>
                    <Button
                      onClick={onViewBookingDetails}
                      title="View Booking Details"
                      style={{ minWidth: 32, padding: "0 8px", marginLeft: 8 }}
                    >
                      ...
                    </Button>
                  </div>
                </InmateInformation>
              )}
            </StyledGridCol>
            {/* Matches ASP and the Jail Incidents equivalent: Different
                Booking only ever appears in Modify mode, not Details/Browse. */}
            {bookingsFieldsEnabled && (
              <StyledGridCol>
                <Button onClick={onDifferentBookingClick} disabled={showDifferentBooking}>
                  Different Booking
                </Button>
              </StyledGridCol>
            )}
          </StyledGridRow>
        )}

        {showDifferentBooking ? (
          <>
            <form>
              <Row>
                <Col lg={12}>
                  <FormItem>
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={searchByBookingNo}
                        onChange={(e) => {
                          setSearchByBookingNo(e.target.checked);
                          setBookingNumberSearch("");
                          setSearchParams({ lastName: "", middleName: "", firstName: "", suffix: "" });
                        }}
                      />
                      Search by Booking Number
                    </label>
                  </FormItem>
                </Col>
              </Row>

              {searchByBookingNo ? (
                <Row>
                  <Col lg={6}>
                    <FormItem label="Booking Number">
                      <TextField
                        value={bookingNumberSearch}
                        onChange={(e) => setBookingNumberSearch(e.target.value)}
                        placeholder="Enter booking number"
                      />
                    </FormItem>
                  </Col>
                  <Col lg={2}>
                    <Button style={{ marginTop: 28 }} onClick={onSearchClick}>Search</Button>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col lg={3}>
                    <FormItem label="Last Name">
                      <TextField name="lastName" value={searchParams.lastName} onChange={onChangeSearchParams} />
                    </FormItem>
                  </Col>
                  <Col lg={3}>
                    <FormItem label="Middle Name">
                      <TextField name="middleName" value={searchParams.middleName} onChange={onChangeSearchParams} />
                    </FormItem>
                  </Col>
                  <Col lg={3}>
                    <FormItem label="First Name">
                      <TextField name="firstName" value={searchParams.firstName} onChange={onChangeSearchParams} />
                    </FormItem>
                  </Col>
                  <Col lg={2}>
                    <FormItem label="Suffix">
                      <Select options={suffixOptions} value={searchParams.suffix} onChange={(opt) => onChangeSearchParams(opt, "suffix")} />
                    </FormItem>
                  </Col>
                  <Col lg={1}>
                    <Button style={{ marginTop: 28 }} onClick={onSearchClick}>Search</Button>
                  </Col>
                </Row>
              )}
            </form>

            <div style={{ marginTop: 20 }}>
              <Table
                key={`details-different-booking-${tableKey}`}
                headers={TABLE_HEADERS}
                data={tableRows}
                totalRecords={tableRows.length}
                isAPiCallLoading={loading}
                pagination={pagination}
                setPagination={setPagination}
                onRowClick={onRowClick}
                noDataMessage="No inmates found"
              />
            </div>
          </>
        ) : (
          detailsRow && (
            <div style={{ marginTop: 20, background: "var(--background-color)" }}>
              <Row>
                <Col lg={3}>
                  <Image
                    alt="Inmate Avatar" className="avatar"
                    src={detailsRow?.bio?.photoUrl || defaultAvatar}
                    width={200} height={200} fallback={defaultAvatar}
                  />
                </Col>
                <Col lg={9}>
                  <InmateDetails fluid>
                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation><div className="key">Arrest Date</div><div className="value">{detailsRow.arrestDate || "-"}</div></InmateInformation>
                      <InmateInformation><div className="key">Scheduled Release Date</div><div className="value">{detailsRow.scheduledReleaseDate || "-"}</div></InmateInformation>
                      <InmateInformation><div className="key">Classification</div><div className="value">{detailsRow.classificationName || "-"}</div></InmateInformation>
                    </Row>
                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation><div className="key">Assigned Location</div><div className="value">{detailsRow.assignedLocation || "-"}</div></InmateInformation>
                      <InmateInformation><div className="key">Current Location</div><div className="value">{detailsRow.currentLocation || "-"}</div></InmateInformation>
                    </Row>
                    <Row style={{ marginTop: "10px" }}>
                      <InmateInformation><div className="key">Pin No</div><div className="value">{detailsRow.inmatePin || "-"}</div></InmateInformation>
                      <InmateInformation><div className="key">Booking Number</div><div className="value">{detailsRow.bookingNumber || "-"}</div></InmateInformation>
                      <InmateInformation><div className="key">DOB</div><div className="value">{detailsRow.dob || "-"}</div></InmateInformation>
                    </Row>
                  </InmateDetails>
                </Col>
              </Row>
            </div>
          )
        )}
      </Modal>

      {/* "..." button target — the linked Booking, read-only, in a popup.
          Same embedding pattern as pages/BookingQueue/index.jsx (a full
          <BookingsPage> inside a Modal instead of navigating away). */}
      {showBookingDetailsModal && (
        <Modal
          designPattern="ant"
          header="Bookings"
          show={showBookingDetailsModal}
          onHide={onCloseBookingDetailsModal}
          size="xxl"
          maskClosable={false}
          rootClassName="custom-modal-mask-dark"
          width="95%"
          footerActions={[
            <Button key="close" onClick={onCloseBookingDetailsModal}>
              Close
            </Button>,
          ]}
        >
          <Suspense fallback={<p>Loading...</p>}>
            {/* fromFolderDetails hides the page's own Modify/Create New/Back
                to List action bar (Bookings/index.jsx:3157) - this popup is
                read-only, same as ASP's "Bookings Details" dialog, which only
                has a Close button. */}
            <BookingsPage fromFolderDetails />
          </Suspense>
        </Modal>
      )}
    </>
  );
};

export default InmateToVisit;